import { Box, type BoxProps } from '@ironhack/design-system2/components/box';
import { PropsWithChildren, type ReactElement } from 'react';

export const ACTIVE_DISPLACEMENT = '2px';

// Use proportional radius on small screens to match scaling horizontal image
export const BUTTON_RADIUS = ['7.3% / 38.1%', null, '3xl'];

export const CARD_TRANSITION = '0.5s ease';

type Props = PropsWithChildren<{ counterName: string }> & BoxProps;

export const AccordionImageButton = (props: Props): ReactElement => {
  const { children, counterName, ...boxProps } = props;

  return (
    <Box
      _active={{
        borderBottomWidth: [ACTIVE_DISPLACEMENT, null, 0],
        borderColor: 'rgb(0 0 0 / 70%)',
        borderRightWidth: [null, null, ACTIVE_DISPLACEMENT],
      }}
      _before={{
        alignItems: 'center',
        background: 'rgb(0 0 0 / 50%)',
        borderRadius: 'full',
        boxSize: '50px',
        color: 'text.inverse',
        content: `counter(${counterName})`,
        counterIncrement: counterName,
        display: 'flex',
        justifyContent: 'center',
        left: [2, null, '50%'],
        position: 'absolute',
        textStyle: '2xl',
        top: ['50%', null, 2],
        transform: ['translateY(-50%)', null, 'translateX(-50%)'],
        transition: ['background-color', 'height', 'left', 'top', 'width']
          .map((prop) => `${prop} 250ms ease`)
          .join(', '),
        zIndex: 1,
      }}
      _hover={{
        _before: {
          boxSize: [null, null, `${50 + 10}px`],
          top: ['50%', null, `${2 * 8 - 10 / 2}px`], // Keeps number still
        },
      }}
      borderRadius={BUTTON_RADIUS}
      height={[null, null, '321px']}
      overflow="hidden"
      position="relative"
      sx={{
        '[open] &': {
          _active: {
            // Only displace when open since we don't want to move whole card
            marginBottom: [`-${ACTIVE_DISPLACEMENT}`, null, 0],
            marginRight: [null, null, `-${ACTIVE_DISPLACEMENT}`],
            transform: `translate(${ACTIVE_DISPLACEMENT}, ${ACTIVE_DISPLACEMENT})`,
          },
          _before: {
            background: 'rgb(0 0 0 / 70%)',
            left: [3, null, '50%'],
          },
          _hover: {
            _before: {
              boxSize: '50px',
              top: ['50%', null, 2],
            },
          },
          height: [null, null, '289px'],
        },
        transition: `height ${CARD_TRANSITION}`,
      }}
      {...boxProps}
    >
      {children}
    </Box>
  );
};
